// Ejemplos: motor de CD con freno y motorreductor con encoder.
import { at, part, wires, UNO } from './helpers.js';

const BB = { id: 'bb1', type: 'breadboard', x: 0, z: 75, rot: 0, props: { size: 'half' } };

// Motorreductor JGA25-370 (12 V, 1:21) con L298N y batería de 12 V: el encoder va a los pines con interrupción 2 y 3.
const gearParts = [
  UNO,
  part('gm1', 'gearmotor', { ratio: 21 }, { x: 105, z: -85, rot: 0 }),
  part('drv', 'l298n', { ena: false, enb: true, reg: true }, { x: 95, z: 5, rot: 0 }),
  part('bat', 'battery', { voltage: '12' }, { x: 32, z: 85, rot: 0 }),
];
const gearWires = wires([
  ['uno:9', 'drv:ENA', 'orange'],
  ['uno:8', 'drv:IN1', 'yellow'],
  ['uno:7', 'drv:IN2', 'green'],
  ['uno:GND.1', 'drv:GND', 'black'],
  ['bat:+', 'drv:+12V', 'red'],
  ['bat:-', 'drv:GND', 'black'],
  ['drv:OUT1', 'gm1:M1', 'red'],
  ['drv:OUT2', 'gm1:M2', 'white'],
  ['uno:5V', 'gm1:VCC', 'red'],
  ['uno:GND.2', 'gm1:GND', 'black'],
  ['uno:2', 'gm1:A', 'yellow'],
  ['uno:3', 'gm1:B', 'green'],
]);

export const EXAMPLES = [
  {
    id: 'motor-freno',
    category: 'Motores',
    name: 'Motor de CD: freno y corriente',
    description: 'Una resistencia de 0.47 Ω mide la corriente de un motor con 4 pilas AA. Sube «Freno en el eje» en el panel de propiedades: la corriente crece hasta la de arranque y el motor se bloquea con el 100 %.',
    project: {
      name: 'Motor con freno',
      files: {
        'sketch.ino': `// Corriente de un motor de CD. Una resistencia de 0.47 Ω entre el − del motor y el GND hace de shunt:
// la caída de voltaje en ella es la corriente (V = I · 0.47 Ω) y cada paso del ADC (4.88 mV) son 10.4 mA.
// Con la simulación en marcha selecciona el motor y sube «Freno en el eje»: el par del motor es proporcional
// a su corriente, así que frenarlo la sube, de ~0.5 A libre a ~1.25 A con el rotor bloqueado (100 %): los 6 V
// de las pilas entre el inducido (3.75 Ω), el shunt y la resistencia interna de las 4 AA (~0.6 Ω).
const int SHUNT = A0;

void setup() {
  Serial.begin(115200);
}

void loop() {
  long suma = 0;
  for (int i = 0; i < 16; i++) suma += analogRead(SHUNT);
  float amperes = suma * 4.88 / 16 / 0.47 / 1000.0;   // mV entre 0.47 Ω = mA
  Serial.print("amperes:");
  Serial.println(amperes, 2);
  delay(50);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('r1', 'resistor', { value: 0.47 }, at(BB, 'e22', 'resistor')),
        part('m1', 'dc-motor', { rotor: 'propeller', brake: 0 }, { x: 110, z: 150, rot: 0 }),
        part('bat', 'battery', { voltage: '6' }, { x: -75, z: 150, rot: 0 }),
      ],
      wires: wires([
        ['uno:GND.2', 'bb1:tn14', 'black'],
        ['bat:+', 'm1:+', 'red'],
        ['bat:-', 'bb1:tn18', 'black'],
        ['m1:-', 'bb1:a22', 'brown'],
        ['bb1:a26', 'bb1:tn22', 'black'],
        ['bb1:b22', 'uno:A0', 'yellow'],
      ]),
    },
  },
  {
    id: 'gearmotor-vueltas',
    category: 'Motores',
    name: 'Motorreductor con encoder: vueltas y sentido',
    description: 'La librería Encoder cuenta los flancos de A y B (cuadratura): 924 cuentas por vuelta del eje con la reducción 1:21. Adelante suma y atrás resta.',
    project: {
      name: 'Motorreductor con encoder',
      files: {
        'sketch.ino': `// Motorreductor JGA25-370 de 12 V (1:21) con encoder Hall, puente H L298N y batería de 12 V.
// El encoder da 11 pulsos por vuelta del rotor en cada canal (A y B); la librería Encoder cuenta los flancos de
// los dos canales: 11 × 4 × 21 = 924 cuentas por vuelta del eje de salida. A y B van a los pines 2 y 3 (interrupciones).
#include <Encoder.h>

const int ENA = 9;
const int IN1 = 8;
const int IN2 = 7;
const float CUENTAS_POR_VUELTA = 11.0 * 4 * 21;

Encoder encoder(2, 3);

void girar(int direccion) {          // 1 = adelante, -1 = atrás, 0 = frena
  digitalWrite(IN1, direccion > 0);
  digitalWrite(IN2, direccion < 0);
  analogWrite(ENA, 255);
}

void informar(const char* texto) {
  long cuentas = encoder.read();
  Serial.print(texto);
  Serial.print(": ");
  Serial.print(cuentas);
  Serial.print(" cuentas = ");
  Serial.print(cuentas / CUENTAS_POR_VUELTA, 2);
  Serial.println(" vueltas");
}

void setup() {
  pinMode(ENA, OUTPUT);
  pinMode(IN1, OUTPUT);
  pinMode(IN2, OUTPUT);
  Serial.begin(9600);
}

void loop() {
  girar(1);
  delay(2000);
  girar(0);                          // frena y espera a que se detenga
  delay(500);
  informar("Después de 2 s adelante");
  girar(-1);
  delay(3000);
  girar(0);
  delay(500);
  informar("Después de 3 s atrás");
}
`,
      },
      parts: gearParts,
      wires: gearWires,
    },
  },
  {
    id: 'gearmotor-pid',
    category: 'Motores',
    name: 'Motorreductor: velocidad con PID',
    description: 'Un PID mantiene la velocidad del eje (rpm) que le pide el programa, midiéndola con las interrupciones del encoder y moviendo el motor con PWM en un L298N. Abre la gráfica para ver el objetivo y la velocidad.',
    project: {
      name: 'PID de velocidad',
      files: {
        'sketch.ino': `// Control de velocidad con PID: motorreductor JGA25-370 (1:21) con encoder, puente H L298N y batería de 12 V.
// Las interrupciones cuentan los flancos de A (pin 2) y B (pin 3): 11 × 4 × 21 = 924 cuentas por vuelta del eje.
// Cada 50 ms se mide la velocidad, el PID corrige el PWM y se imprime «objetivo» y «velocidad» para la gráfica.
const int ENA = 9;
const int IN1 = 8;
const int IN2 = 7;
const int PIN_A = 2;
const int PIN_B = 3;
const float CUENTAS_POR_VUELTA = 11.0 * 4 * 21;

// Velocidades (rpm del eje) que pide el programa; cambian cada 4 s
const float OBJETIVOS[] = { 120, 60, 150, 90 };

const float KP = 0.9;
const float KI = 5.0;
const float KD = 0.0;

volatile long cuentas = 0;
volatile uint8_t previo = 0;

// Cuadratura: con el giro hacia adelante B adelanta a A (+1) y atrás ocurre al revés (−1)
void leerEncoder() {
  static const int8_t tabla[16] = { 0, 1, -1, 0, -1, 0, 0, 1, 1, 0, 0, -1, 0, -1, 1, 0 };
  uint8_t actual = (digitalRead(PIN_A) << 1) | digitalRead(PIN_B);
  cuentas += tabla[(previo << 2) | actual];
  previo = actual;
}

void setup() {
  pinMode(ENA, OUTPUT);
  pinMode(IN1, OUTPUT);
  pinMode(IN2, OUTPUT);
  pinMode(PIN_A, INPUT_PULLUP);
  pinMode(PIN_B, INPUT_PULLUP);
  digitalWrite(IN1, HIGH);           // siempre hacia adelante
  digitalWrite(IN2, LOW);
  previo = (digitalRead(PIN_A) << 1) | digitalRead(PIN_B);
  attachInterrupt(digitalPinToInterrupt(PIN_A), leerEncoder, CHANGE);
  attachInterrupt(digitalPinToInterrupt(PIN_B), leerEncoder, CHANGE);
  Serial.begin(115200);
}

void loop() {
  static unsigned long anterior = 0;
  static long cuentasAnteriores = 0;
  static float integral = 0;
  static float errorAnterior = 0;

  unsigned long ahora = millis();
  if (ahora - anterior < 50) return;
  float dt = (ahora - anterior) / 1000.0;
  anterior = ahora;

  noInterrupts();
  long c = cuentas;
  interrupts();
  float rpm = (c - cuentasAnteriores) / CUENTAS_POR_VUELTA * 60.0 / dt;
  cuentasAnteriores = c;

  float objetivo = OBJETIVOS[(ahora / 4000) % 4];
  float error = objetivo - rpm;
  integral = constrain(integral + error * dt, -50, 50);      // límite contra el exceso de integral
  float salida = KP * error + KI * integral + KD * (error - errorAnterior) / dt;
  errorAnterior = error;
  analogWrite(ENA, constrain(salida, 0, 255));

  Serial.print("objetivo:");
  Serial.print(objetivo);
  Serial.print(" velocidad:");
  Serial.println(rpm);
}
`,
      },
      parts: gearParts,
      wires: gearWires,
    },
  },
];
